"use client";

import { useEffect, useMemo, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { Icon } from "@/lib/icons/Icon";
import { Field, FileUpload, FormStatus, SubmitButton, Textarea } from "@/components/ui/Form";
import { Badge } from "@/components/ui/Badge/Badge";
import { confirmDialog } from "@/lib/notifications/confirmDialog";
import { toast } from "@/lib/notifications/toast";
import {
  fetchTaskDetail,
  updateProjectTask,
  type TaskDetail as TaskDetailData,
  type TaskFile,
  type TaskUpdateAction,
} from "@/lib/api/c-crm-project-management-user";
import styles from "./CCrmProjectManagement.module.css";

/** Limit zalacznikow - musi zgadzac sie z `MAX_TASK_FILES` w komponencie PHP,
    ktory sprawdza go ponownie po stronie serwera. */
const MAX_TASK_FILES = 10;

/**
 * Szczegóły zadania z pełną historią wpisów (plugin c_crm_project_management).
 *
 * Odpowiednik widoku zadania z panelu administracyjnego - informacje o zadaniu,
 * załączniki, podzadania i oś czasu wszystkich wpisów.
 *
 * **Widoczność wpisów liczy backend, nie ten komponent.** Historia w Imperialu jest
 * zależna od ról zespołu projektu: rola oznaczona
 * `m_crm_team_roles.show_info_only_from_flow_roles` jest widoczna wyłącznie dla osób,
 * które mają ją w swoim `workflow`. Wpis zamknięty przychodzi z `available: false`,
 * BEZ treści i autora - front nie ma czego ukrywać, renderuje zaślepkę w miejscu,
 * w którym wpis stoi na osi czasu. Dzięki temu klient widzi, że praca trwa, nie
 * poznając jej wewnętrznych szczegółów.
 *
 * `history_locked_count` pokazujemy jawnie także wtedy, gdy konfiguracja CRM
 * (`hide_workflow_info`) wycięła zaślepki - inaczej niepełna historia wyglądałaby
 * na kompletną.
 *
 * HTML (`description`, `text` wpisu) pochodzi z edytora panelu - renderowany przez
 * `dangerouslySetInnerHTML`, spójnie z CBlog/CNews/CAdvertisements.
 *
 * **Załączniki graficzne** otwierają się w `yet-another-react-lightbox` (ta sama
 * biblioteka co galerie CMS i zdjęcia ogłoszeń). Lightbox jest JEDEN na cały widok
 * i zbiera obrazy z załączników zadania ORAZ ze wszystkich wpisów historii w kolejności
 * wyświetlania - dzięki temu klient przewija je strzałkami jak galerię, zamiast
 * zamykać i otwierać każdy z osobna. Pliki nieobrazkowe zostają zwykłym linkiem
 * pobrania, tak samo jak obraz bez `href` (plik zniknął z dysku).
 */

/** Y-m-d H:i:s -> Y-m-d H:i (bez sekund; historia potrzebuje godziny, lista nie). */
function formatDateTime(raw: string): string {
  if (!raw) return "";
  const [date, time] = raw.split(" ");
  if (!time) return date || raw;
  return `${date} ${time.slice(0, 5)}`;
}

/**
 * Godziny w czytelnej formie. Pelne godziny bez czesci dziesietnej, ulamki
 * z jednym miejscem - "2 h" zamiast "2.0 h", ale "1.5 h" zostaje.
 */
function formatHours(hours: number): string {
  if (!Number.isFinite(hours)) return "-";
  const value = Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
  return `${value} h`;
}

function FileRow({ file, onOpenImage }: { file: TaskFile; onOpenImage?: () => void }) {
  const label = `${file.name}${file.size ? ` (${file.size} MB)` : ""}`;
  return (
    <li className={styles.fileRow}>
      <Icon name={file.is_image ? "image" : "file-text"} />
      {onOpenImage ? (
        //Obraz - podgląd w lightboxie zamiast wychodzenia z panelu do nowej karty.
        <button type="button" className={styles.fileLinkBtn} onClick={onOpenImage}>
          {label}
        </button>
      ) : file.href ? (
        <a href={file.href} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      ) : (
        //Brak href = pliku fizycznie nie ma na dysku; pokazujemy nazwę bez linku.
        <span>{label}</span>
      )}
      {file.description && <span className={styles.fileDesc}>{file.description}</span>}
    </li>
  );
}

export function TaskDetail({
  idTask,
  onBack,
}: {
  idTask: number;
  onBack: () => void;
}) {
  const [data, setData] = useState<TaskDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  //Indeks otwartego obrazu w lightboxie; null = zamkniety.
  const [openAt, setOpenAt] = useState<number | null>(null);
  //Formularz aktualizacji: tresc odpowiedzi + blokada na czas wysylki.
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState<TaskUpdateAction | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [replyFiles, setReplyFiles] = useState<File[]>([]);

  /* Plaska lista obrazow w KOLEJNOSCI WYSWIETLANIA (zalaczniki zadania, potem
     zalaczniki kolejnych wpisow historii). Mapa href -> indeks pozwala kazdemu
     wierszowi otworzyc lightbox na wlasnej pozycji, a strzalki przewijaja przez
     caly zestaw. Liczone przed wczesnymi returnami - hooki musza byc bezwarunkowe. */
  const { slides, indexByHref } = useMemo(() => {
    const sources: string[] = [];

    const collect = (files: TaskFile[]) => {
      for (const f of files) {
        if (f.is_image && f.href && !sources.includes(f.href)) sources.push(f.href);
      }
    };

    if (data) {
      collect(data.task.files);
      for (const entry of data.history) collect(entry.files);
    }

    return {
      slides: sources.map((src) => ({ src })),
      indexByHref: new Map(sources.map((src, i) => [src, i])),
    };
  }, [data]);

  /** Handler otwarcia lightboxa dla pliku - undefined gdy plik nie jest obrazem. */
  const openImage = (file: TaskFile) => {
    if (!file.is_image || !file.href) return undefined;
    const index = indexByHref.get(file.href);
    if (index === undefined) return undefined;
    return () => setOpenAt(index);
  };

  /* Bez resetowania stanu na starcie efektu - komponent jest montowany z `key={idTask}`
     przez wołającego, więc inne zadanie to nowa instancja ze świeżym stanem
     początkowym (loading=true, error=null). Ustawianie tego tutaj byłoby
     kaskadowym renderem bez efektu. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const detail = await fetchTaskDetail(idTask);
        if (cancelled) return;
        setData(detail);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [idTask]);

  /**
   * Wysyla aktualizacje i przeladowuje szczegoly, zeby historia i status byly swieze.
   * Zamkniecie zadania jest nieodwracalne z panelu klienta, wiec prosi o potwierdzenie.
   */
  async function runUpdate(action: TaskUpdateAction) {
    setUpdateError(null);

    if (action === "complete") {
      const confirmed = await confirmDialog({
        title: "Uznać zadanie za zrealizowane?",
        message:
          "Zadanie zostanie zamknięte. Jeśli sprawa wróci, trzeba będzie zgłosić ją ponownie albo poprosić nas o wznowienie.",
        confirmLabel: "Uznaj za zrealizowane",
        variant: "warning",
      });
      if (!confirmed) return;
    }

    setBusy(action);
    try {
      const res = await updateProjectTask({
        id_task: idTask,
        action,
        text: replyText.trim() || undefined,
        files: replyFiles,
      });
      toast.success(
        action === "reply"
          ? "Odpowiedź została zapisana."
          : `Zadanie ${res.task.nr} zostało zamknięte.`,
      );
      setReplyText("");
      setReplyFiles([]);
      //Swieze dane - nowy wpis ma pojawic sie w historii, a status w naglowku.
      setData(await fetchTaskDetail(idTask));
    } catch (e) {
      setUpdateError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return (
      <div className={styles.root}>
        <FormStatus variant="info">Wczytywanie zadania...</FormStatus>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={styles.root}>
        <FormStatus variant="error">{error ?? "Nie udało się wczytać zadania."}</FormStatus>
        <button type="button" className={styles.pageButton} onClick={onBack}>
          <Icon name="caret-left" />
          Wróć do listy
        </button>
      </div>
    );
  }

  const { task, history } = data;

  return (
    <div className={styles.root}>
      <div className={styles.panelHeader}>
        <button type="button" className={styles.backButton} onClick={onBack}>
          <Icon name="caret-left" />
          Zadania
        </button>
        <h3 className={styles.panelTitle}>
          {task.name} <span className={styles.panelNr}>({task.nr})</span>
        </h3>
      </div>

      <dl className={styles.detailGrid}>
        <div className={styles.detailItem}>
          <dt>Typ</dt>
          <dd>{task.type_name ?? "-"}</dd>
        </div>
        <div className={styles.detailItem}>
          <dt>Status</dt>
          <dd>{task.status_name ?? "-"}</dd>
        </div>
        <div className={styles.detailItem}>
          <dt>Priorytet</dt>
          <dd>{task.priority_name ?? "-"}</dd>
        </div>
        <div className={styles.detailItem}>
          <dt>Przydzielone do</dt>
          <dd>{task.assigned_to_fullname ?? "Nieprzydzielone"}</dd>
        </div>
        <div className={styles.detailItem}>
          <dt>Zgłosił</dt>
          <dd>{task.owner_fullname ?? "-"}</dd>
        </div>
        <div className={styles.detailItem}>
          <dt>Zgłoszono</dt>
          <dd>{formatDateTime(task.add_date) || "-"}</dd>
        </div>
        {task.start_date && (
          <div className={styles.detailItem}>
            <dt>Start</dt>
            <dd>{formatDateTime(task.start_date)}</dd>
          </div>
        )}
        {task.end_date && (
          <div className={styles.detailItem}>
            <dt>Termin</dt>
            <dd>{formatDateTime(task.end_date)}</dd>
          </div>
        )}
        {/* Sprawdzenie TYPU, nie `!== null` - endpoint sprzed tej zmiany (albo starszy
            cache routes) w ogole nie zwraca tego pola, a `undefined !== null` przechodzi
            i wywala formatowanie. Front musi znosic odpowiedz bez nowego pola. */}
        {typeof task.time_worked_client === "number" && (
          <div className={styles.detailItem}>
            <dt>Przepracowany czas</dt>
            <dd>{formatHours(task.time_worked_client)}</dd>
          </div>
        )}
        {task.project && (
          <div className={styles.detailItem}>
            <dt>Projekt</dt>
            <dd>{task.project.name}</dd>
          </div>
        )}
      </dl>

      <div className={styles.progressBar}>
        <div className={styles.progressFill} style={{ width: `${task.percent_complete}%` }} />
        <span className={styles.progressLabel}>Postęp: {task.percent_complete}%</span>
      </div>

      {task.to_acceptance && (
        <FormStatus variant="warning">To zadanie oczekuje na akceptację.</FormStatus>
      )}

      {task.description && (
        <section className={styles.detailSection}>
          <h4 className={styles.detailSectionTitle}>Opis</h4>
          <div
            className={styles.richText}
            dangerouslySetInnerHTML={{ __html: task.description }}
          />
        </section>
      )}

      {task.files.length > 0 && (
        <section className={styles.detailSection}>
          <h4 className={styles.detailSectionTitle}>Załączniki</h4>
          <ul className={styles.fileList}>
            {task.files.map((f, i) => (
              <FileRow key={`${f.name}-${i}`} file={f} onOpenImage={openImage(f)} />
            ))}
          </ul>
        </section>
      )}

      {task.child_tasks.length > 0 && (
        <section className={styles.detailSection}>
          <h4 className={styles.detailSectionTitle}>Podzadania</h4>
          <ul className={styles.childList}>
            {task.child_tasks.map((c) => (
              <li key={c.id} className={styles.childRow}>
                <span className={styles.name}>
                  {c.name} <span className={styles.panelNr}>({c.nr})</span>
                </span>
                <Badge>{c.status_name}</Badge>
                <span className={styles.childPercent}>{c.percent_complete}%</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className={styles.detailSection}>
        <h4 className={styles.detailSectionTitle}>Aktualizacja zadania</h4>

        {!data.can_update ? (
          <FormStatus variant="info">
            {data.update_blocked_reason === "task_closed"
              ? "Zadanie jest zamknięte - aby wrócić do sprawy, zgłoś ją ponownie lub skontaktuj się z nami."
              : "Ten projekt nie ma przydzielonego menadżera projektu, więc nie ma komu przekazać aktualizacji. Powiadomiliśmy o tym nasz zespół - prosimy o kontakt, jeśli sprawa jest pilna."}
          </FormStatus>
        ) : (
          <>
            <Field
              label="Odpowiedź"
              hint="Odpowiedź trafi do menadżera projektu, który pokieruje sprawą dalej."
            >
              <Textarea
                rows={4}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                disabled={busy !== null}
              />
            </Field>

            <FileUpload
              label="Załączniki"
              optional
              multiple
              value={replyFiles}
              onChange={setReplyFiles}
              maxFiles={MAX_TASK_FILES}
              disabled={busy !== null}
              hint="Zrzuty ekranu, dokumenty - możesz też wkleić zrzut ze schowka (Ctrl+V)."
            />

            {data.project_manager && (
              <p className={styles.assignedInfo}>
                <Icon name="user" />
                Po wysłaniu zadanie trafi do menadżera projektu:{" "}
                <strong>{data.project_manager.fullname}</strong>
              </p>
            )}

            {updateError && <FormStatus variant="error">{updateError}</FormStatus>}

            <div className={styles.formActions}>
              <SubmitButton
                type="button"
                loading={busy === "reply"}
                disabled={busy !== null || replyText.trim() === ""}
                onClick={() => runUpdate("reply")}
              >
                Wyślij odpowiedź
              </SubmitButton>
              {/* Zamkniecie nie wymaga tresci - komentarz jest opcjonalny. */}
              <SubmitButton
                variant="link"
                type="button"
                loading={busy === "complete"}
                disabled={busy !== null}
                onClick={() => runUpdate("complete")}
              >
                Uznaj za zrealizowane
              </SubmitButton>
            </div>
          </>
        )}
      </section>

      <section className={styles.detailSection}>
        <h4 className={styles.detailSectionTitle}>Historia zadania</h4>

        {data.history_locked_count > 0 && (
          <p className={styles.lockedNote}>
            <Icon name="info" />
            {data.history_hidden
              ? `${data.history_locked_count} wpisów jest niedostępnych ze względu na przepływ informacji w systemie.`
              : "Część wpisów jest oznaczona jako niedostępna - to wewnętrzna korespondencja zespołu."}
          </p>
        )}

        {history.length === 0 && (
          <FormStatus variant="info">Brak wpisów w historii tego zadania.</FormStatus>
        )}

        <ol className={styles.historyList}>
          {history.map((entry) => (
            <li
              key={entry.id}
              className={entry.available ? styles.historyItem : styles.historyItemLocked}
            >
              <div className={styles.historyMeta}>
                <span className={styles.historyIndex}>#{entry.index}</span>
                <span className={styles.historyAuthor}>
                  {entry.available ? (entry.author ?? "Nieznany użytkownik") : "Wpis wewnętrzny"}
                </span>
                <span className={styles.historyDate}>{formatDateTime(entry.add_date)}</span>
              </div>

              {!entry.available && (
                <p className={styles.historyLockedText}>
                  <Icon name="lock" />
                  Treść wpisu nie jest dostępna ze względu na przepływ informacji w systemie.
                </p>
              )}

              {entry.available && entry.changes && (
                /* Log zmian jest HTML-em (moduł otacza stare i nowe wartości `<strong>`),
                   a backend go sanityzuje - renderujemy tak samo jak panel admina,
                   zamiast pokazywać surowe znaczniki. Łamanie linii ogarnia CSS. */
                <div
                  className={styles.historyChanges}
                  dangerouslySetInnerHTML={{ __html: entry.changes }}
                />
              )}

              {entry.available && entry.text && (
                <div
                  className={styles.richText}
                  dangerouslySetInnerHTML={{ __html: entry.text }}
                />
              )}

              {entry.available && entry.files.length > 0 && (
                <ul className={styles.fileList}>
                  {entry.files.map((f, i) => (
                    <FileRow key={`${f.name}-${i}`} file={f} onOpenImage={openImage(f)} />
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ol>
      </section>

      <Lightbox
        open={openAt !== null}
        index={openAt ?? 0}
        close={() => setOpenAt(null)}
        slides={slides}
      />
    </div>
  );
}
